import React, { useEffect, useMemo, useRef, useState, useCallback, useContext } from 'react';
import { Link, useParams, useRouteMatch, useLocation, Switch, Route } from 'react-router-dom';
import { Dropdown } from 'react-bootstrap';
import FetchContainer from 'Utils/FetchContainer';
import AppContext from 'Utils/AppContext';
import { getAdvancedConfig } from 'AdvancedConfig/api';
import { STATUS, INK, FONT_MONO } from 'Utils/styleTokens';
import { listDiscoverJobsPage } from 'DataDiscovery/discoverApi';
import { getDataSourceTags, setDataSourceTag } from 'DataSources/datasourcesApi';
import {
  listMaskingJobs, listEncryptJobs, listDecryptJobs, deriveMaskStatusByDataSource,
  mergeEncryptionJobsForDataSource, mapEncryptionRowStatus,
} from 'Secure/api';
import exportFromJSON from 'export-from-json';
import {
  getEnterpriseSummary, getAllEnterpriseSourceDiffPages, DIFF_LOAD_CAP, isDiffUnsupportedError,
} from '../edlApi';
import {
  SEVERITY_STYLE, runLabel, sourceTypeLabel, diffRowKey, isRemediable, buildHandoffRows,
  levelMapFromTiers, diffTotalsIn, countUnitForSource, countNoun,
} from '../edlUtils';
import {
  SOURCE_STATE, STATE_TAG_KEY, STATE_LABELS, STATE_HELP, STATE_PILL, deriveState, storedState,
} from '../stateTag';
import AreaOverview from './AreaOverview';
import FindingsList from './FindingsList';

// "from" label when the run is compared against an empty baseline (only one completed discovery,
// or the user picked "baseline" in the from picker): every row is NEW, nothing resolved/persisting.
const BASELINE_LABEL = 'baseline (no findings)';

/**
 * Per-source shell: owns the run pair, the FULL diff for that pair, the severity tiers and the
 * handoff selection, and renders one of two views beneath a shared header —
 *
 *   /enterprise/source/:id            the area overview ("where inside this source")
 *   /enterprise/source/:id/findings   the per-(location, entity) ledger, grouped by area
 *
 * The data lives here rather than in either child so that navigating between them neither
 * refetches the diff nor drops the user's selection: picking an area on the overview ticks its
 * open rows, and the ledger opens with them already selected.
 */
const SourceDetail = () => {
  const { id } = useParams();
  const { path, url } = useRouteMatch();
  const location = useLocation();
  // The enterprise summary has no rootPath, but the globally loaded connections do — and the
  // area derivation needs it to fold a document span's ABSOLUTE path onto the same folder key
  // as a structured finding's root-relative one. Same accessor CandidateSourcesPanel uses.
  const { connections } = useContext(AppContext);
  const [source, setSource] = useState(null);
  const [runs, setRuns] = useState([]);
  // One state object: setting `to` and `from` separately would render (and fetch a baseline diff)
  // in between, since setState outside an event handler is not batched in React 16.
  const [pair, setPair] = useState({ fromId: null, toId: null });
  const { fromId, toId } = pair;
  const setFromId = (v) => setPair((prev) => ({ ...prev, fromId: v }));
  const setToId = (v) => setPair((prev) => ({ ...prev, toId: v }));
  const [diff, setDiff] = useState(null);
  const [diffError, setDiffError] = useState(null);
  // True when the comparison is impossible rather than broken (see isDiffUnsupportedError) — the
  // views then explain it in the ordinary text colour instead of shouting a failure.
  const [diffUnsupported, setDiffUnsupported] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingDiff, setLoadingDiff] = useState(false);
  // {loaded, total} while the diff pages in — a real denominator, because the first response's
  // rowCount is the exact unfiltered total.
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState(null);
  // Selected diff-row keys (see diffRowKey). Cleared whenever the compared pair changes, but NOT
  // when the user moves between the overview and the ledger.
  const [selected, setSelected] = useState(() => new Set());
  // entity → landscape severity level (Critical/High/Medium/Low) from advanced config; best-effort.
  const [levelByEntity, setLevelByEntity] = useState({});
  // The source's remediation state tag (see ../stateTag). `derivedState` is what automation says
  // it is; `state` is what is shown — the same thing unless the user has set it to MANAGED.
  const [sourceState, setSourceState] = useState(null);
  const [derivedState, setDerivedState] = useState(SOURCE_STATE.UNKNOWN);
  const [stateBusy, setStateBusy] = useState(false);
  // resolveState runs detached from the load effect, so it checks this before touching state.
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  /**
   * Work out the source's state from the protection jobs and the landscape severity, show it, and
   * persist it as the `state` tag so the landscape home can lay the grid out by it without
   * re-deriving anything. A source the user has marked MANAGED is never overwritten — that is the
   * whole point of the flag — but we still keep `derivedState` around so "Set to Auto" has
   * something to fall back to.
   */
  const resolveState = useCallback(async (summarySource) => {
    let tags = {};
    let maskStatus = null;
    let encrypted = false;
    try {
      const [tagMap, maskRes, encRes, decRes] = await Promise.all([
        getDataSourceTags(id).catch(() => ({})),
        listMaskingJobs({ page: 0, size: 200 }).catch(() => null),
        listEncryptJobs({ page: 0, size: 200 }).catch(() => null),
        listDecryptJobs({ page: 0, size: 200 }).catch(() => null),
      ]);
      tags = tagMap || {};
      maskStatus = deriveMaskStatusByDataSource(maskRes?.content || [])[Number(id)] || null;
      const merged = mergeEncryptionJobsForDataSource(
        encRes?.content || [], decRes?.content || [], Number(id)
      );
      encrypted = mapEncryptionRowStatus(merged) === 'ENCRYPTED';
    } catch (e) {
      // Best-effort: with no protection history the derivation still has severity to go on.
    }
    const derived = deriveState({ severity: summarySource?.severity, maskStatus, encrypted });
    const stored = storedState(tags);
    if (!mounted.current) return;
    setDerivedState(derived);
    setSourceState(stored === SOURCE_STATE.MANAGED ? SOURCE_STATE.MANAGED : derived);
    if (stored !== SOURCE_STATE.MANAGED && stored !== derived) {
      try {
        await setDataSourceTag(id, STATE_TAG_KEY, derived);
      } catch (e) {
        console.log('Could not store the data-source state tag', e);
      }
    }
  }, [id]);

  /** Actions → Set to Managed / Set to Auto. */
  const applyState = async (next) => {
    setStateBusy(true);
    try {
      await setDataSourceTag(id, STATE_TAG_KEY, next);
      if (mounted.current) setSourceState(next);
    } catch (e) {
      console.log('Could not set the data-source state tag', e);
    }
    setStateBusy(false);
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [summary, jobs, cfg] = await Promise.all([
          getEnterpriseSummary(),
          listDiscoverJobsPage(0, 50, 'startedAt,desc', id).catch(() => ({ discoverJobs: [] })),
          getAdvancedConfig().catch(() => null),
        ]);
        if (!alive) return;
        setLevelByEntity(levelMapFromTiers(cfg?.landscapeSeverity?.tiers));
        const found = (summary.sources || []).find((s) => String(s.id) === String(id)) || null;
        setSource(found);
        // Opening this page is what refreshes the source's state tag (see ../stateTag). Fire and
        // forget: a failed tag write must not stop the diff from rendering.
        resolveState(found);
        const completed = (jobs.discoverJobs || []).filter((j) => j.status === 'SUCCEEDED');
        setRuns(completed);
        // The summary decides what counts as a run here: the landscape covers core C2PP discovery
        // only, so a source whose every run was executed by an integration worker comes back with
        // no latestRun and IS unscanned as far as this page goes. Falling back to the raw job list
        // in that case would pick exactly the run the platform refuses to diff. The list is still
        // the fallback when the summary has no record of the source at all.
        const to = found ? (found.latestRun?.jobId ?? null) : (completed[0]?.id ?? null);
        const from = found ? (found.previousRun?.jobId ?? null) : (completed[1]?.id ?? null);
        setPair({ fromId: from, toId: to });
        if (!found) setError('This data source is not on the Enterprise Data Landscape.');
      } catch (e) {
        if (alive) setError(e?.message || 'Failed to load the data source');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [id, resolveState]);

  useEffect(() => {
    if (toId == null || fromId === toId) {
      setDiff(null);
      return undefined;
    }
    let alive = true;
    setLoadingDiff(true);
    setDiffError(null);
    setDiffUnsupported(false);
    setProgress(null);
    setSelected(new Set());
    (async () => {
      try {
        // fromId == null → baseline: the API omits `from` and the worker diffs against an empty run.
        // Every page is loaded up front: the area rollup has to see all rows to be accurate.
        const full = await getAllEnterpriseSourceDiffPages(id, {
          from: fromId,
          to: toId,
          onProgress: (loaded, total) => { if (alive) setProgress({ loaded, total }); },
        });
        if (!alive) return;
        setDiff(full);
      } catch (e) {
        if (alive) {
          setDiff(null);
          // The platform's own message names the worker and what to do about it; the ApiError's
          // own message is just "GET <url> failed: 501", which tells the user nothing.
          setDiffError(e?.body?.message || e?.message || 'Could not compare these runs');
          setDiffUnsupported(isDiffUnsupportedError(e));
        }
      } finally {
        if (alive) { setLoadingDiff(false); setProgress(null); }
      }
    })();
    return () => { alive = false; };
  }, [id, fromId, toId]);

  const rows = useMemo(() => diff?.rows || [], [diff]);

  // Source as the views see it: the summary record plus the scan root from the connection.
  const sourceWithRoot = useMemo(() => {
    if (!source) return source;
    const conn = (connections || []).find((c) => String(c?.id) === String(source.id));
    const rootPath = conn?.config?.rootPath || conn?.rootPath || null;
    return rootPath ? { ...source, rootPath } : source;
  }, [source, connections]);

  const toggleRow = useCallback((row) => {
    if (!isRemediable(row)) return;
    const key = diffRowKey(row);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  /** Tick (or untick) a batch of rows at once — the ledger's per-area heading controls and the
   *  overview's row click both go through here. RESOLVED rows are never selectable. */
  const setRowsSelected = useCallback((batch, on) => {
    setSelected((prev) => {
      const next = new Set(prev);
      (batch || []).filter(isRemediable).forEach((r) => {
        const key = diffRowKey(r);
        if (on) next.add(key); else next.delete(key);
      });
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelected(new Set()), []);

  const selectedRows = useMemo(() => rows.filter((r) => selected.has(diffRowKey(r))), [rows, selected]);
  const openRows = useMemo(() => rows.filter(isRemediable), [rows]);

  const fromRun = runs.find((r) => r.id === fromId)
    || (source?.previousRun?.jobId === fromId ? { id: fromId, finishedAt: source.previousRun.completedAt } : null);
  const toRun = runs.find((r) => r.id === toId)
    || (source?.latestRun?.jobId === toId ? { id: toId, finishedAt: source.latestRun.completedAt } : null);
  const fromCompletedAt = diff?.fromCompletedAt || fromRun?.finishedAt || null;
  const toCompletedAt = diff?.toCompletedAt || toRun?.finishedAt || null;

  const isBaseline = fromId == null && toId != null;
  const fromLabel = isBaseline
    ? BASELINE_LABEL
    : runLabel(fromRun ? { jobId: fromRun.id, completedAt: fromRun.finishedAt } : null);
  const toLabel = runLabel(toRun ? { jobId: toRun.id, completedAt: toRun.finishedAt } : null);
  const sev = SEVERITY_STYLE[source?.severity] || SEVERITY_STYLE.NEVER_SCANNED;

  // Counted in the source's own unit so the headline agrees with the same source's Data Source
  // Risk Profile — distinct columns for a database, total findings for a file source. The
  // server's `totals` (and the summary's cached `diff`) are (location, entity) ROW counts, which
  // is a third number again, so they are deliberately not used here: these are derived from the
  // rows actually loaded, which is also what makes the `truncated` banner's caveat true.
  const noun = countNoun(source);
  const totals = useMemo(
    () => (diff ? diffTotalsIn(rows, countUnitForSource(source)) : null),
    [diff, rows, source],
  );

  const generateHandoff = () => {
    if (selectedRows.length === 0) return;
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    const safeName = String(source?.name || id).replace(/[\\/:*?"<>|]+/g, '_').replace(/\s+/g, '-');
    exportFromJSON({
      data: buildHandoffRows(selectedRows, {
        sourceName: source?.name || '', fromLabel, toLabel, levelByEntity, fromCompletedAt, toCompletedAt,
      }),
      fileName: `handoff-${safeName}-${stamp}`,
      exportType: exportFromJSON.types.csv,
    });
  };

  const pickerOptions = runs.length ? runs : [
    ...(source?.latestRun ? [{ id: source.latestRun.jobId, finishedAt: source.latestRun.completedAt }] : []),
    ...(source?.previousRun ? [{ id: source.previousRun.jobId, finishedAt: source.previousRun.completedAt }] : []),
  ];

  const onFindings = location.pathname.endsWith('/findings');

  // Everything the two views need. Grouped so a new child doesn't mean threading 20 props.
  const viewProps = {
    id,
    url,
    source: sourceWithRoot,
    rows,
    diff,
    diffError,
    diffUnsupported,
    loadingDiff,
    progress,
    toId,
    levelByEntity,
    selected,
    toggleRow,
    setRowsSelected,
    clearSelection,
    selectedRows,
    openRows,
    fromLabel,
    toLabel,
    isBaseline,
    fromCompletedAt,
    toCompletedAt,
  };

  return (
    <FetchContainer fetchingData={loading} absolute={true} error={error}>
      <div className='edl-page'>
        <div className='d-flex justify-content-between align-items-center' style={{ gap: 20 }}>
          <div className='edl-breadcrumb'>
            <Link to='/enterprise'>Enterprise Data Landscape</Link>
            <span style={{ color: INK[200] }}> / </span>
            {onFindings ? (
              <>
                <Link to={url} style={{ fontFamily: FONT_MONO, fontSize: 12 }}>{source?.name || `#${id}`}</Link>
                <span style={{ color: INK[200] }}> / </span>
                <span style={{ color: INK[700], fontSize: 12 }}>Findings</span>
              </>
            ) : (
              <span style={{ color: INK[700], fontFamily: FONT_MONO, fontSize: 12 }}>{source?.name || `#${id}`}</span>
            )}
          </div>
          <div className='sg-page-header__actions' style={{ marginBottom: 10 }}>
            <Dropdown alignRight={true}>
              <Dropdown.Toggle data-testid='page-header-actions-toggle' size='sm' className='pr-4 pl-4' variant='outline-secondary'>
                Actions
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item
                  key='handoff'
                  disabled={selectedRows.length === 0}
                  title={selectedRows.length === 0 ? 'Select at least one open finding' : 'Download the selected findings as CSV'}
                  onClick={generateHandoff}
                  data-testid='edl-handoff'
                >
                  Download CSV{selectedRows.length > 0 ? ` (${selectedRows.length})` : ''}
                </Dropdown.Item>
                <Dropdown.Divider />
                {/* The state tag's only manual control: hand the source to a person, or hand it
                    back to derivation. Everything else about the state is derived on load. */}
                <Dropdown.Item
                  key='state-managed'
                  disabled={stateBusy || sourceState === SOURCE_STATE.MANAGED}
                  title={STATE_HELP[SOURCE_STATE.MANAGED]}
                  onClick={() => applyState(SOURCE_STATE.MANAGED)}
                  data-testid='edl-state-managed'
                >
                  Set state to Managed
                </Dropdown.Item>
                <Dropdown.Item
                  key='state-auto'
                  disabled={stateBusy || sourceState !== SOURCE_STATE.MANAGED}
                  title={`Let C2 derive the state again (currently ${STATE_LABELS[derivedState]})`}
                  onClick={() => applyState(derivedState)}
                  data-testid='edl-state-auto'
                >
                  Set state to Auto
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>

        <div className='d-flex justify-content-between align-items-start flex-wrap' style={{ gap: 20, marginBottom: 18 }}>
          <div style={{ minWidth: 0 }}>
            <div className='d-flex align-items-center flex-wrap' style={{ gap: 10 }}>
              <h1 style={{ font: `500 21px/28px ${FONT_MONO}`, letterSpacing: '-.01em', color: INK[900], margin: 0 }}>
                {source?.name || `Data source #${id}`}
              </h1>
              {source && (
                <span className='sg-severity' style={{ background: sev.pillBg, color: sev.pillFg }} data-testid='edl-severity-pill'>
                  {sev.label}
                </span>
              )}
              {source && sourceState && (
                <span
                  className='sg-severity'
                  style={{ background: STATE_PILL[sourceState].bg, color: STATE_PILL[sourceState].fg }}
                  title={STATE_HELP[sourceState]}
                  data-testid='edl-state-pill'
                  data-state={sourceState}
                >
                  {STATE_LABELS[sourceState]}
                </span>
              )}
            </div>
            <div className='sg-small' style={{ marginTop: 8 }}>
              {source ? sourceTypeLabel(source) : ''}
              {toRun && (fromRun || isBaseline) && (
                <span> <span style={{ color: INK[200] }}>•</span> {isBaseline ? 'Baseline' : `Run ${fromLabel}`} → Run {toLabel}</span>
              )}
              {source && <span> <span style={{ color: INK[200] }}>•</span> <Link to={`/landscape/riskprofile/${source.id}`}>Data Source Risk Profile</Link></span>}
            </div>
          </div>
          <div className='edl-run-picker'>
            <select aria-label='Compare from run' data-testid='edl-run-from' value={fromId ?? ''}
              onChange={(e) => setFromId(e.target.value ? Number(e.target.value) : null)}>
              <option value=''>— baseline (no findings) —</option>
              {pickerOptions.map((r) => <option key={r.id} value={r.id}>{runLabel({ jobId: r.id, completedAt: r.finishedAt })}</option>)}
            </select>
            <span style={{ color: INK[400] }}>→</span>
            <select aria-label='Compare to run' data-testid='edl-run-to' value={toId ?? ''}
              onChange={(e) => setToId(e.target.value ? Number(e.target.value) : null)}>
              <option value=''>— to —</option>
              {pickerOptions.map((r) => <option key={r.id} value={r.id}>{runLabel({ jobId: r.id, completedAt: r.finishedAt })}</option>)}
            </select>
          </div>
        </div>

        <div className='edl-kpis'>
          <div className='edl-kpi' style={{ background: STATUS.success.bg, color: STATUS.success.text }}>
            <div className='edl-kpi__label'>↓ Resolved</div>
            <div className='edl-kpi__big' data-testid='edl-kpi-resolved'>{totals ? totals.resolved.toLocaleString() : '—'}</div>
            <div className='edl-kpi__foot'>{isBaseline ? 'nothing resolved yet' : `${noun} resolved`}</div>
          </div>
          <div className='edl-kpi' style={{ background: STATUS.warning.bg, color: STATUS.warning.text }}>
            <div className='edl-kpi__label'>Persisting</div>
            <div className='edl-kpi__big' data-testid='edl-kpi-persisting'>{totals ? totals.persisting.toLocaleString() : '—'}</div>
            <div className='edl-kpi__foot'>{noun} unchanged</div>
          </div>
          <div className='edl-kpi' style={{ background: STATUS.danger.bg, color: STATUS.danger.text }}>
            <div className='edl-kpi__label'>↑ New</div>
            <div className='edl-kpi__big' data-testid='edl-kpi-new'>{totals ? totals.new.toLocaleString() : '—'}</div>
            <div className='edl-kpi__foot'>{noun} found</div>
          </div>
        </div>

        {diff?.truncated && (
          <div className='edl-banner' data-testid='edl-truncated'>
            Showing the first {rows.length.toLocaleString()} of {diff.rowCount.toLocaleString()} changes
            (the page loads at most {DIFF_LOAD_CAP.toLocaleString()}). Counts below cover the loaded rows only.
          </div>
        )}

        <Switch>
          <Route exact path={path}>
            <AreaOverview {...viewProps} />
          </Route>
          <Route path={`${path}/findings`}>
            <FindingsList {...viewProps} />
          </Route>
        </Switch>
      </div>
    </FetchContainer>
  );
};

export default SourceDetail;
